<template>
  <v-app>
    <v-main class="app-background">
      <!-- Mientras el router resuelve la primera navegación (revisa la sesión), la ruta aún no tiene meta:
           no se pinta nada, para que no aparezca el menú lateral un instante antes del login. -->
      <div v-if="!routerReady" class="app-booting" aria-hidden="true"></div>
      <template v-else>
      <ImpersonationBanner v-if="impersonation" :info="impersonation" />
      <div v-if="route.meta.public || route.meta.fullscreen" class="app-public-layout">
        <router-view />
      </div>
      <div v-else-if="route.meta.superadmin" class="app-layout app-layout--admin">
        <AdminSideBar />
        <div class="app-right">
          <AdminTopBar :title="route.meta.title || ''" />
          <router-view />
        </div>
      </div>
      <div v-else-if="isTenant" class="app-layout app-layout--tenant" :class="{ 'app-layout--imp': impersonation }">
        <TenantSideBar />
        <div class="app-right">
          <TenantTopBar :title="route.meta.title || ''" />
          <router-view />
        </div>
        <TenantBottomNav />
      </div>
      <div v-else class="app-layout" :class="{ 'app-layout--tech': isTechnician, 'app-layout--imp': impersonation }">
        <SideBar :class="{ 'app-sidebar--tech': isTechnician }" />
        <div class="app-right">
          <router-view />
        </div>
        <TechnicianBottomNav v-if="isTechnician" />
        <AppTour v-if="!isTechnician" />
      </div>
      </template>
    </v-main>
  </v-app>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import SideBar from '@/components/Global/SideBar.vue'
import AppTour from '@/components/Global/AppTour.vue'
import TechnicianBottomNav from '@/components/Technician/TechnicianBottomNav.vue'
import TenantSideBar from '@/components/TenantPortal/TenantSideBar.vue'
import TenantTopBar from '@/components/TenantPortal/TenantTopBar.vue'
import TenantBottomNav from '@/components/TenantPortal/TenantBottomNav.vue'
import AdminSideBar from '@/components/Superadmin/AdminSideBar.vue'
import AdminTopBar from '@/components/Superadmin/AdminTopBar.vue'
import ImpersonationBanner from '@/components/Superadmin/ImpersonationBanner.vue'
import { useAuthStore } from '@/services/authStore'

const route = useRoute()
const router = useRouter()
const routerReady = ref(false)
router.isReady().then(() => { routerReady.value = true }).catch(() => { routerReady.value = true })
const { state } = useAuthStore()
// El técnico usa el celular en campo: en móvil su menú es una barra inferior, no el sidebar.
const isTechnician = computed(() => state.user?.role === 'TECHNICIAN')
// El inquilino tiene su propio portal: sidebar con la identidad del parque y barra inferior en móvil.
const isTenant = computed(() => state.user?.kind === 'TENANT')
// "Ver como este parque": banner fijo mientras un superadmin está dentro de la cuenta de un cliente.
const impersonation = computed(() => (route.meta.superadmin ? null : state.user?.impersonation || null))
</script>

<style>
* {
  box-sizing: border-box;
}

body {
  font-family: 'Open Sans', sans-serif !important;
  margin: 0;
}

.v-application {
  background: var(--bg-base) !important;
}

.app-layout {
  position: fixed;
  inset: 0;
  display: flex;
  background-color: var(--bg-base);
  overflow: hidden;
}

/* El banner de impersonación ocupa los primeros 48px de la pantalla. */
.app-layout--imp {
  top: 48px;
}

.app-right {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  min-height: 0;
  height: 100%;
}

.app-booting {
  position: fixed;
  inset: 0;
  background-color: var(--bg-base);
}

.app-public-layout {
  position: fixed;
  inset: 0;
  overflow: auto;
}

@media (max-width: 768px) {
  .app-sidebar--tech { display: none; }
  .app-layout--tenant .app-right { padding-bottom: calc(56px + env(safe-area-inset-bottom)); }
  .app-layout--tech .app-right { padding-bottom: calc(56px + env(safe-area-inset-bottom)); }
}
</style>